/**
 * MFA Setup Notification Utilities
 *
 * Handles detection and deduplication of MFA setup notifications.
 * Uses localStorage with TTL for cross-tab coordination.
 *
 * IMPORTANT: The mfa_just_configured claim is ONLY present in the ID token,
 * not in the /userinfo endpoint response. This is intentional because it's
 * a transient claim that should only appear once after MFA setup.
 */

const MFA_NOTIFICATION_SHOWN_KEY = 'mfa_notification_shown_at';

// TTL matches ID token lifetime (1 hour) to prevent duplicate notifications
// within the same auth session while allowing future MFA setups to show it
const MFA_NOTIFICATION_TTL_MS = 60 * 60 * 1000;

/**
 * Decode the payload from a JWT token.
 * Does NOT verify the signature - only extracts the claims.
 *
 * @param token - The JWT token string
 * @returns The decoded payload claims, or null if decoding fails
 */
export function decodeJwtPayload(token: string): Record<string, any> | null {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Base64Url decode the payload (second part)
    const payload = parts[1];
    // Handle base64url encoding (replace - with + and _ with /)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if MFA was just configured and return the type.
 *
 * NOTE: The mfa_just_configured claim is in the ID token, NOT in userData
 * from the /userinfo endpoint. We must decode the ID token to check this claim.
 *
 * @param idToken - The raw ID token JWT string
 * @returns MFA type if notification should be shown, null otherwise
 */
export function shouldShowMFANotification(
  idToken: string | null
): string | null {
  if (!idToken) {
    return null;
  }

  const claims = decodeJwtPayload(idToken);
  const mfaClaim = claims?.['mfa_just_configured'];

  // Check if MFA was just configured (boolean flag for backwards compatibility)
  if (mfaClaim !== true) {
    return null;
  }

  // Check deduplication
  const shownAt = localStorage.getItem(MFA_NOTIFICATION_SHOWN_KEY);
  const cutoff = Date.now() - MFA_NOTIFICATION_TTL_MS;

  if (shownAt && parseInt(shownAt, 10) > cutoff) {
    return null;
  }

  // Get MFA type from standard OIDC amr (Authentication Methods References) claim
  // The amr claim is an array that contains authentication methods used, e.g. ["otp", "mfa", "sms"]
  const amr = claims?.['amr'];
  let mfaType = 'unknown'; // default fallback when amr is not available

  if (Array.isArray(amr)) {
    if (amr.includes('sms')) {
      mfaType = 'sms';
    } else if (amr.includes('totp')) {
      mfaType = 'totp';
    }
  }

  return mfaType;
}

/**
 * Mark MFA notification as shown (for deduplication).
 * Call this immediately after showing the notification.
 */
export function markMFANotificationShown(): void {
  localStorage.setItem(MFA_NOTIFICATION_SHOWN_KEY, Date.now().toString());
}

/**
 * Get the notification TTL in milliseconds.
 * Exported for testing purposes.
 */
export function getMFANotificationTTL(): number {
  return MFA_NOTIFICATION_TTL_MS;
}

/**
 * Get the localStorage key used for tracking.
 * Exported for testing purposes.
 */
export function getMFANotificationKey(): string {
  return MFA_NOTIFICATION_SHOWN_KEY;
}
